import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function JoinScreen() {
  const { code: urlCode } = useParams();
  const [tab, setTab] = useState("new"); // 'new' | 'rejoin'
  const [code, setCode] = useState(urlCode || "");
  const [recovery, setRecovery] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const join = async () => {
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/join", { code: code.trim().toUpperCase() });
      localStorage.setItem("dm_participant_id", data.participant_id);
      navigate(`/play/${data.participant_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nepodařilo se připojit");
    } finally {
      setLoading(false);
    }
  };

  const rejoin = async () => {
    if (!code.trim() || !recovery.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/rejoin", {
        code: code.trim().toUpperCase(),
        recovery_code: recovery.trim(),
      });
      localStorage.setItem("dm_participant_id", data.participant_id);
      toast.success(`Vítej zpátky, ${data.nickname}!`);
      navigate(`/play/${data.participant_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recovery kód nesedí");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream flex flex-col relative overflow-hidden">
      <div className="absolute inset-x-0 top-28 pointer-events-none opacity-[0.07] select-none">
        <div className="whitespace-nowrap flex animate-marquee">
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className="text-[18vw] font-heading font-black tracking-tighter mx-6 outline-headline">
              DIGITÁLNÍ MOST • DIGITÁLNÍ MOST •{" "}
            </span>
          ))}
        </div>
      </div>

      <header className="relative z-10 flex items-center justify-between p-5 sm:p-8">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-orange border-2 border-black shadow-brut-sm" />
          <span className="font-heading font-black tracking-tight text-lg">digitální most</span>
        </div>
        <Link
          to="/teacher/login"
          className="text-sm font-bold underline underline-offset-4 hover:text-brand-orange"
          data-testid="link-teacher-login"
        >
          Jsem lektor →
        </Link>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-5 pb-20">
        <div className="w-full max-w-md space-y-6">
          <div className="inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-1.5 rounded-full text-xs font-bold shadow-brut-sm">
            <span className="w-2 h-2 bg-brand-mint rounded-full animate-pulse" />
            Připoj se do workshopu
          </div>
          <h1 className="text-5xl sm:text-6xl font-heading font-black tracking-tighter leading-[0.9]">
            {tab === "new" ? (
              <>Zadej <span className="bg-brand-yellow border-2 border-black px-2 inline-block -rotate-1">kód</span> od lektora.</>
            ) : (
              <>Vracíš se <span className="bg-brand-cyan border-2 border-black px-2 inline-block rotate-1">zpátky</span>?</>
            )}
          </h1>
          <p className="text-black/70 leading-relaxed">
            {tab === "new"
              ? "Nic se neregistruje. Jen napiš kód, vytvoř si postavu a jedeme."
              : "Měl/a jsi výpadek? Zadej session kód a svůj recovery kód, pokračuješ tam, kdes skončil/a."}
          </p>

          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setTab("new")}
              className={`brut-btn flex-1 ${tab === "new" ? "bg-black text-white" : "bg-white"}`}
              data-testid="tab-new"
            >
              Poprvé
            </button>
            <button
              onClick={() => setTab("rejoin")}
              className={`brut-btn flex-1 ${tab === "rejoin" ? "bg-black text-white" : "bg-white"}`}
              data-testid="tab-rejoin"
            >
              Vracím se
            </button>
          </div>

          <div className="brut-card p-5 space-y-4">
            <div>
              <label className="text-xs font-black uppercase tracking-wider">Session kód (4 písmena)</label>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^A-Za-z]/g, "").toUpperCase().slice(0, 4))}
                maxLength={4}
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                placeholder="ABCD"
                className="mt-1 w-full text-4xl font-mono font-black text-center tracking-[0.4em] border-2 border-black rounded-xl py-4 focus:outline-none focus:bg-brand-yellow uppercase"
                data-testid="input-session-code"
                onKeyDown={(e) => e.key === "Enter" && (tab === "new" ? join() : recovery && rejoin())}
              />
              <div className="text-[11px] text-black/50 mt-1.5 leading-snug">
                Stačí 4 písmena, žádná čísla. Žádný NumLock není potřeba.
              </div>
            </div>

            {tab === "rejoin" && (
              <div>
                <label className="text-xs font-black uppercase tracking-wider">Tvůj recovery kód (6 čísel)</label>
                <input
                  value={recovery}
                  onChange={(e) => setRecovery(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="123456"
                  className="mt-1 w-full text-2xl font-mono font-bold text-center tracking-[0.3em] border-2 border-black rounded-xl py-3 focus:outline-none focus:bg-brand-cyan"
                  data-testid="input-recovery-code"
                  onKeyDown={(e) => e.key === "Enter" && rejoin()}
                />
                <div className="text-[11px] text-black/50 mt-1.5 leading-snug">
                  Recovery kód jsi viděl/a v lobby. Pokud ho neznáš, lektor ti ho najde u tvé přezdívky.
                </div>
              </div>
            )}

            <button
              onClick={tab === "new" ? join : rejoin}
              disabled={loading || !code || (tab === "rejoin" && recovery.length < 4)}
              className={`brut-btn w-full text-lg disabled:opacity-50 ${tab === "new" ? "bg-brand-orange text-white" : "bg-brand-cyan text-black"}`}
              data-testid="btn-join"
            >
              {loading ? "Pracuji..." : tab === "new" ? "Vejít →" : "Pokračovat ↺"}
            </button>
          </div>

          {tab === "new" && (
            <div className="grid grid-cols-3 gap-3 pt-2">
              <div className="brut-card p-3 text-xs font-bold text-center">
                <div className="text-lg">🎭</div>
                Vlastní avatar
              </div>
              <div className="brut-card p-3 text-xs font-bold text-center">
                <div className="text-lg">⏱️</div>
                Živá hodina
              </div>
              <div className="brut-card p-3 text-xs font-bold text-center">
                <div className="text-lg">🛡️</div>
                Anonymně
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
